import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HPSummarySectionCard } from 'components/sections/hitter-library/profile/summary/sections/section-card';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { isAfter, parseISO } from 'date-fns';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitterProfileHelper } from 'lib_ts/classes/hitter-profile.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
} from 'recharts';

export const HitterProfileSummaryTotalPitchTypes = () => {
  const currSummaryHits = useHitterProfileStore((s) => s.currSummaryHits);
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  // hit counts per pitch type
  const data = useMemo((): { count: number; type: PitchType | 'None' }[] => {
    const currentStart = HitterProfileHelper.getCurrentStartDate(
      hitsFilter.period
    );

    const currentHits = currSummaryHits.filter((h) =>
      isAfter(parseISO(h._created), currentStart)
    );

    const asPitchTypes = currentHits.map((h) => h.pitch?.type);

    const asDict = ArrayHelper.groupByFn(
      asPitchTypes,
      (m) => m ?? PitchType.None
    );

    // keys are pitch types, like 'FF', 'FC', etc...
    const output = Object.keys(asDict)
      .sort((a, b) => {
        // ensure None is always last
        if (a === PitchType.None) {
          return 1;
        }

        if (b === PitchType.None) {
          return -1;
        }

        return StringHelper.safeSort(a, b);
      })
      .map((key) => ({
        count: asDict[key]?.length ?? 0,
        type: key as PitchType,
      }));

    return output;
  }, [currSummaryHits, hitsFilter.period]);

  return (
    <ErrorBoundary identifier="HitterProfileSummaryTotalPitchTypes">
      <HPSummarySectionCard loading={loading}>
        <Flex gap={RADIX.FLEX.GAP.LG} direction="column" height="100%">
          <Box>
            <Flex justify="between" align="center">
              <Heading size="3">{t('hitters.total-pitch-types-faced')}</Heading>
            </Flex>
            <Text color={RADIX.COLOR.NEUTRAL}>
              {t('hitters.showing-pitch-types-faced')}
            </Text>
          </Box>

          <ResponsiveContainer width="100%" minHeight={260}>
            <BarChart data={data}>
              <CartesianGrid vertical={false} stroke="var(--gray-6)" />
              <Bar dataKey="count" fill="var(--accent-a7)" radius={8}>
                <LabelList
                  dataKey="count"
                  position="top"
                  dy={-4}
                  style={{ fontSize: 12 }}
                />
              </Bar>
              <XAxis
                dataKey="type"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                padding={{ left: 8, right: 8 }}
                height={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </Flex>
      </HPSummarySectionCard>
    </ErrorBoundary>
  );
};
